import { motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { HiMapPin } from 'react-icons/hi2';
import { IoCloseSharp } from 'react-icons/io5';
import 'swiper/css';
import 'swiper/css/navigation';
import { Keyboard, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useAuth } from '../../../components/context/Info';
import LeadContactModal from '../../../components/modals/LeadContactModal';
import { getEmpreendimentoServerSideProps } from '../../../lib/server/site-props';
import { imoveis_empreendimento } from '../../../services/properties';
import { getEmpreendimentoById } from '../../../services/selectors';
import { isEmpreendimentoVisibleOnSite } from '../../../utils/empreendimentos';
import { formatSelects } from '../../../utils/FunctionsGlobals';
import { appendCodsiteQuery, buildCanonical } from '../../../utils/url';
import RecommendationPiecesProperties from '../../imoveis/components/pieces/properties/recommendation/RecommendationPiecesProperties';
import WelcomePiecesProperties2 from '../../imoveis/components/pieces/properties/welcome2/WelcomePiecesProperties';
import styles from './empreendimento.module.css';

const AboutPieces = dynamic(() => import('../../../components/pieces/about/AboutPieces'), { ssr: false });
const AboutPieces1 = dynamic(() => import('../../../components/pieces/about1/AboutPieces1'), { ssr: false });
const AboutPieces2 = dynamic(() => import('../../../components/pieces/about1/AboutPieces2'), { ssr: false });
const Mapa = dynamic(() => import('../../../components/pieces/mapa/mapa'), { ssr: false });
const DepoimentoCard2 = dynamic(() => import('../../../components/carousel/depoimentos/DepoimentosCard2'), { ssr: false });
const DepoimentoCard = dynamic(() => import('../../../components/carousel/depoimentos/DepoimentosCard'), { ssr: false });

export const getServerSideProps = getEmpreendimentoServerSideProps;

interface FotoEmpreendimento {
    id: number;
    url_foto: string;
    descricao?: string | null;
    destaque?: number | null;
    posicao?: number | null;
    categoria?: string | null;
}

interface EmpreendimentoListaDetalhe {
    id?: number | null;
    nome?: string | null;
    codsite?: number | null;
}

interface EmpreendimentoCategoriaDetalhe {
    id_categoria?: number | null;
    nome?: string | null;
    codsite?: number | null;
}

interface EmpreendimentoData {
    id: number;
    nome: string;
    id_tipos?: number | null;
    endereco?: string | null;
    cep?: string | null;
    cidade?: string | null;
    bairro?: string | null;
    uf?: string | null;
    Estado?: string | null;
    estado?: string | null;
    id_estado?: number | string | null;
    caracteristicas_detalhes?: EmpreendimentoListaDetalhe[] | null;
    tipos_emp_detalhes?: EmpreendimentoListaDetalhe[] | null;
    categoria_empreendimento?: EmpreendimentoCategoriaDetalhe | null;
    construtora?: string | null;
    descricao?: string | null;
    Descricao_interna?: string | null;
    situacao?: string | null;
    preco_min?: number | null;
    preco_max?: number | null;
    are_privativa_min?: string | null;
    area_privativa_min?: string | null;
    area_privativa_max?: string | null;
    dormitorios?: number | null;
    suites?: number | null;
    banheiros?: number | null;
    vagas?: number | null;
    unidades?: number | null;
    torres?: number | null;
    andares?: number | null;
    mostrar_site?: number | boolean | string | null;
    logo?: string | null;
    meta_description?: string | null;
    meta_keywords?: string | null;
    meta_title?: string | null;
    latitude_longitude?: string | null;
    fotos?: FotoEmpreendimento[] | null;
}

interface EmpreendimentoPageProps {
    empreendimento?: EmpreendimentoData | null;
    allDataHome?: any;
    prodOrigin?: string;
    codsite?: number;
    asPath?: string;
}

const fallbackImage = 'https://fotos.sobressai.com.br/Fotos/imovel_sem_foto.jpg';

const parseIdParam = (value: string | string[] | undefined): number | null => {
    const raw = Array.isArray(value) ? value[0] : value;
    if (!raw) return null;
    const parsed = Number(String(raw).replace(/[^\d]/g, ''));
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
};

const stripHtml = (value: string | null | undefined) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const normalizeLocationPart = (value: string | number | null | undefined) =>
    String(value || '')
        .replace(/\s+/g, ' ')
        .replace(/\s*,\s*,+/g, ', ')
        .replace(/^[,\s]+|[,\s]+$/g, '')
        .trim();

const joinLocationParts = (parts: Array<string | number | null | undefined>) =>
    parts
        .map((part) => normalizeLocationPart(part))
        .filter(Boolean)
        .join(', ')
        .replace(/\s*,\s*,+/g, ', ')
        .replace(/^[,\s]+|[,\s]+$/g, '')
        .trim();

const limitText = (text: string, max: number) => {
    if (!text) return '';
    if (text.length <= max) return text;
    return `${text.slice(0, max).trimEnd()}...`;
};

const parseAreaNumber = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return null;
    const parsed = Number(String(value).replace(',', '.').replace(/[^\d.]/g, ''));
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
};

const parseLatLngPair = (value: string | null | undefined): [number, number] | null => {
    const source = String(value || '').trim();
    if (!source) return null;

    const numbers = source
        .replace(/point/gi, '')
        .match(/-?\d+(?:[.,]\d+)?/g)
        ?.map((item) => Number(item.replace(',', '.')));

    if (!numbers || numbers.length < 2) return null;

    let lat = Number(numbers[0]);
    let lng = Number(numbers[1]);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    if (Math.abs(lat) > 90 && Math.abs(lng) <= 90) {
        const swap = lat;
        lat = lng;
        lng = swap;
    }

    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;

    return [lat, lng];
};

const toMapaLatLng = (value: string | null | undefined) => {
    const coords = parseLatLngPair(value);
    if (!coords) return '';
    return `(${coords[0]},${coords[1]})`;
};

const formatCurrency = (value: number | null | undefined) => {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount <= 0) return '';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(amount);
};

const formatNumber = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: value % 1 !== 0 ? 1 : 0,
        maximumFractionDigits: value % 1 !== 0 ? 1 : 0
    }).format(value);

const formatAreaRange = (areaMin: string | number | null | undefined, areaMinAlt: string | number | null | undefined, areaMax: string | number | null | undefined) => {
    const min = parseAreaNumber(areaMin) || parseAreaNumber(areaMinAlt);
    const max = parseAreaNumber(areaMax);

    if (min && max) {
        if (min === max) return `${formatNumber(min)} m²`;
        return `${formatNumber(min)} à ${formatNumber(max)} m²`;
    }
    if (min) return `a partir de ${formatNumber(min)} m²`;
    if (max) return `até ${formatNumber(max)} m²`;
    return '';
};
const formatPriceRange = (minValue: number | null | undefined, maxValue: number | null | undefined) => {
    const min = formatCurrency(minValue);
    const max = formatCurrency(maxValue);

    if (min && max) {
        if (min === max) return min;
        return `${min} a ${max}`;
    }
    if (min) return `a partir de ${min}`;
    if (max) return `ate ${max}`;
    return '';
};

const formatSituacao = (value: string | null | undefined) => {
    const key = String(value || '').toLowerCase();
    const map: Record<string, string> = {
        emconstrucao: 'Em construção',
        lancamento: 'Lançamento',
        pronto: 'Pronto para morar'
    };
    return map[key] || String(value || 'Disponível');
};

const normalizePhone = (value: string | null | undefined) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('55')) return digits;
    if (digits.length >= 10) return `55${digits}`;
    return '';
};

type RgbColor = { r: number; g: number; b: number };

const rgbToHex = ({ r, g, b }: RgbColor) =>
    `#${[r, g, b]
        .map((channel) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0'))
        .join('')}`;

const parseRgbColor = (value: string | null | undefined): RgbColor | null => {
    const source = String(value || '').trim().toLowerCase();
    if (!source) return null;

    if (source.startsWith('#')) {
        const normalized = source.slice(1);
        if (/^[0-9a-f]{3}$/.test(normalized)) {
            return {
                r: parseInt(normalized[0] + normalized[0], 16),
                g: parseInt(normalized[1] + normalized[1], 16),
                b: parseInt(normalized[2] + normalized[2], 16)
            };
        }

        if (/^[0-9a-f]{6}$/.test(normalized)) {
            return {
                r: parseInt(normalized.slice(0, 2), 16),
                g: parseInt(normalized.slice(2, 4), 16),
                b: parseInt(normalized.slice(4, 6), 16)
            };
        }
    }

    const rgbMatch = source.match(/^rgba?\(([^)]+)\)$/i);
    if (!rgbMatch) return null;

    const parts = rgbMatch[1].split(',').map((part) => Number(part.trim()));
    if (parts.length < 3 || parts.slice(0, 3).some((part) => !Number.isFinite(part))) return null;

    return {
        r: parts[0],
        g: parts[1],
        b: parts[2]
    };
};

const sanitizeColor = (value: string | null | undefined, fallback: string) => {
    const parsed = parseRgbColor(value);
    if (!parsed) return fallback;
    return rgbToHex(parsed);
};

const getLuminance = (value: string) => {
    const rgb = parseRgbColor(value);
    if (!rgb) return 1;

    const normalizeChannel = (channel: number) => {
        const normalized = channel / 255;
        return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
    };

    const r = normalizeChannel(rgb.r);
    const g = normalizeChannel(rgb.g);
    const b = normalizeChannel(rgb.b);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const getContrastRatio = (first: string, second: string) => {
    const lum1 = getLuminance(first);
    const lum2 = getLuminance(second);
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    return (lighter + 0.05) / (darker + 0.05);
};

const getReadableTextColor = (background: string, light = '#ffffff', dark = '#10223a') =>
    getContrastRatio(background, light) >= getContrastRatio(background, dark) ? light : dark;

const getBestTextForBackgrounds = (backgrounds: string[], light = '#ffffff', dark = '#10223a') => {
    const safeBackgrounds = backgrounds.filter(Boolean);
    if (safeBackgrounds.length === 0) return dark;

    const lightScore = Math.min(...safeBackgrounds.map((background) => getContrastRatio(background, light)));
    const darkScore = Math.min(...safeBackgrounds.map((background) => getContrastRatio(background, dark)));
    return lightScore >= darkScore ? light : dark;
};

const isColorClose = (first: string, second: string, minDistance = 44) => {
    const rgbFirst = parseRgbColor(first);
    const rgbSecond = parseRgbColor(second);
    if (!rgbFirst || !rgbSecond) return first.toLowerCase() === second.toLowerCase();

    const distance = Math.sqrt(
        (rgbFirst.r - rgbSecond.r) ** 2 +
        (rgbFirst.g - rgbSecond.g) ** 2 +
        (rgbFirst.b - rgbSecond.b) ** 2
    );

    return distance < minDistance;
};

const getSafeAccentColor = (candidate: string, background: string) => {
    if (!isColorClose(candidate, background, 52)) return candidate;
    return getLuminance(background) > 0.58 ? '#0a3f67' : '#dbeafe';
};

const decodeHtmlEntities = (value: string) =>
    value
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>');

const normalizeFeatureText = (value: string) =>
    decodeHtmlEntities(stripHtml(value))
        .replace(/^[-*•]+\s*/, '')
        .replace(/\s+/g, ' ')
        .trim();

const uniqueOrdered = (items: string[]) => {
    const seen = new Set<string>();
    const unique: string[] = [];
    items.forEach((item) => {
        const key = item.toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        unique.push(item);
    });
    return unique;
};

const extractCaracteristicas = (html: string | null | undefined) => {
    const source = String(html || '');
    if (!source.trim()) return [];

    const liItems = Array.from(source.matchAll(/<li[^>]*>(.*?)<\/li>/gis))
        .map((match) => normalizeFeatureText(match[1] || ''))
        .filter(Boolean);

    if (liItems.length > 0) {
        return uniqueOrdered(liItems).slice(0, 24);
    }

    const normalized = decodeHtmlEntities(source)
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|h[1-6]|li|ul|ol)>/gi, '\n')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\u00a0/g, ' ');

    const tokens = normalized
        .split(/\n|;|,/)
        .map((chunk) => normalizeFeatureText(chunk))
        .filter((chunk) => {
            if (!chunk) return false;
            const wordCount = chunk.split(/\s+/).length;
            if (wordCount > 6) return false;
            if (chunk.length < 3 || chunk.length > 46) return false;
            return true;
        });

    return uniqueOrdered(tokens).slice(0, 24);
};

const isPlantaFoto = (foto: FotoEmpreendimento) => String(foto?.categoria || '').trim().toLowerCase() === 'planta';

type ResumoIconType = 'dormitorios' | 'area' | 'banheiros' | 'vagas' | 'bairro';

const ResumoTagIcon = ({ type }: { type: ResumoIconType }) => {
    if (type === 'bairro') {
        return <HiMapPin className={styles.tagIcon} />;
    }

    if (type === 'dormitorios') {
        return (
            <svg viewBox="0 0 640 512" className={styles.tagIcon} fill="currentColor" aria-hidden="true">
                <path d="M176 256c44.11 0 80-35.89 80-80s-35.89-80-80-80-80 35.89-80 80 35.89 80 80 80zm352-128H304c-8.84 0-16 7.16-16 16v144H64V80c0-8.84-7.16-16-16-16H16C7.16 64 0 71.16 0 80v352c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16v-48h512v48c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16V240c0-61.86-50.14-112-112-112z" />
            </svg>
        );
    }

    if (type === 'banheiros') {
        return (
            <svg viewBox="0 0 512 512" className={styles.tagIcon} fill="currentColor" aria-hidden="true">
                <path d="M32 384a95.4 95.4 0 0 0 32 71.09V496a16 16 0 0 0 16 16h32a16 16 0 0 0 16-16v-16h256v16a16 16 0 0 0 16 16h32a16 16 0 0 0 16-16v-40.91A95.4 95.4 0 0 0 480 384v-48H32zm464-128H80V69.25a21.26 21.26 0 0 1 36.28-15l19.27 19.26c-13.13 29.88-7.61 59.11 8.62 79.73l-.17.17a16 16 0 0 0 0 22.59l11.31 11.31a16 16 0 0 0 22.63 0L283.31 81.94a16 16 0 0 0 0-22.63L272 48a16 16 0 0 0-22.62 0l-.17.17c-20.62-16.23-49.83-21.75-79.73-8.62l-19.26-19.27A69.25 69.25 0 0 0 32 69.25V256H16a16 16 0 0 0-16 16v16a16 16 0 0 0 16 16h480a16 16 0 0 0 16-16v-16a16 16 0 0 0-16-16z" />
            </svg>
        );
    }

    if (type === 'vagas') {
        return (
            <svg viewBox="0 0 512 512" className={styles.tagIcon} fill="currentColor" aria-hidden="true">
                <path d="M499.99 176h-59.87l-16.64-41.6C406.38 91.63 365.57 64 319.5 64h-127c-46.06 0-86.88 27.63-103.99 70.4L71.87 176H12.01C4.2 176-1.53 183.34.37 190.91l6 24C7.7 220.25 12.5 224 18.01 224h20.07C24.65 235.73 16 252.78 16 272v48c0 16.12 6.16 30.67 16 41.93V416c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32v-32h256v32c0 17.67 14.33 32 32 32h32c17.67 0 32-14.33 32-32v-54.07c9.84-11.25 16-25.8 16-41.93v-48c0-19.22-8.65-36.27-22.07-48H494c5.51 0 10.31-3.75 11.64-9.09l6-24c1.89-7.57-3.84-14.91-11.65-14.91zm-352.06-17.83c7.29-18.22 24.94-30.17 44.57-30.17h127c19.63 0 37.28 11.95 44.57 30.17L384 208H128l19.93-49.83zM96 319.8c-19.2 0-32-12.76-32-31.9S76.8 256 96 256s48 28.71 48 47.85-28.8 15.95-48 15.95zm320 0c-19.2 0-48 3.19-48-15.95S396.8 256 416 256s32 12.76 32 31.9-12.8 31.9-32 31.9z" />
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 512 512" className={styles.tagIcon} fill="currentColor" aria-hidden="true">
            <path d="M488 352h-40V109.25l59.31-59.31c6.25-6.25 6.25-16.38 0-22.63L484.69 4.69c-6.25-6.25-16.38-6.25-22.63 0L402.75 64H192v96h114.75L160 306.75V24c0-13.26-10.75-24-24-24H88C74.75 0 64 10.74 64 24v40H24C10.75 64 0 74.74 0 88v48c0 13.25 10.75 24 24 24h40v264c0 13.25 10.75 24 24 24h232v-96H205.25L352 205.25V488c0 13.25 10.75 24 24 24h48c13.25 0 24-10.75 24-24v-40h40c13.25 0 24-10.75 24-24v-48c0-13.26-10.75-24-24-24z" />
        </svg>
    );
};

const EmpreendimentoPage = ({ empreendimento, allDataHome, prodOrigin, codsite, asPath }: EmpreendimentoPageProps) => {
    const router = useRouter();
    const { favorito, setFavorito, isMobile1 } = useAuth();
    const site = allDataHome?.site;
    const pageThemeVars = useMemo(
        () => {
            const background = sanitizeColor(site?.cor5, '#ffffff');
            const paper = sanitizeColor(site?.cor5 || site?.cor4, '#ffffff');
            const surfaceSoft = sanitizeColor(site?.cor5, '#f4f7fa');
            const textBase = sanitizeColor(site?.cor11, '#1a2f42');
            const text = getContrastRatio(background, textBase) >= 3 ? textBase : getReadableTextColor(background);

            const mutedBase = sanitizeColor(site?.cor14 || site?.cor10, '#5f6f7d');
            const muted = getContrastRatio(background, mutedBase) >= 2.5 && !isColorClose(background, mutedBase, 28)
                ? mutedBase
                : (getLuminance(background) > 0.58 ? '#5f6f7d' : '#cbd5e1');

            const lineBase = sanitizeColor(site?.cor14 || site?.cor10, '#cbd5e1');
            const line = !isColorClose(background, lineBase, 28)
                ? lineBase
                : (getLuminance(background) > 0.58 ? '#d0d5dd' : '#475467');

            const accentRaw = sanitizeColor(site?.cor2, '#0a3f67');
            const accent = getSafeAccentColor(accentRaw, background);
            const accentSoftBase = sanitizeColor(site?.cor1 || site?.cor6, '#1f1f1f');
            const accentSoftSeed = getSafeAccentColor(accentSoftBase, background);
            const accentSoft = isColorClose(accentSoftSeed, accent, 34)
                ? (getLuminance(accent) > 0.58 ? '#0a3f67' : '#1f3b52')
                : accentSoftSeed;

            const accentInk = getReadableTextColor(accent);
            const accentSoftInk = getReadableTextColor(accentSoft);
            const secondaryInkCandidate = isColorClose(accent, surfaceSoft, 38) ? getReadableTextColor(surfaceSoft) : accent;
            const secondaryInk = getContrastRatio(surfaceSoft, secondaryInkCandidate) >= 3
                ? secondaryInkCandidate
                : getReadableTextColor(surfaceSoft);
            const whatsappInkCandidate = isColorClose(accent, paper, 38) ? getReadableTextColor(paper) : accent;
            const whatsappInk = getContrastRatio(paper, whatsappInkCandidate) >= 3
                ? whatsappInkCandidate
                : getReadableTextColor(paper);
            const mapBandInk = getBestTextForBackgrounds([accent, accentSoft]);

            const mapButtonBase = sanitizeColor(site?.cor13 || site?.cor12 || '#ffffff', '#ffffff');
            const mapButtonFallback = mapBandInk === '#ffffff' ? '#ffffff' : '#10223a';
            const mapButtonBg = isColorClose(mapButtonBase, accent, 48) && isColorClose(mapButtonBase, accentSoft, 48)
                ? mapButtonFallback
                : mapButtonBase;
            const mapButtonInk = getReadableTextColor(mapButtonBg);
            const mapButtonBorder = isColorClose(mapButtonBg, accent, 32) ? line : mapButtonBg;

            return {
                '--site-cor1': site?.cor1 || undefined,
                '--site-cor2': site?.cor2 || undefined,
                '--site-cor3': site?.cor3 || undefined,
                '--site-cor4': site?.cor4 || undefined,
                '--site-cor5': site?.cor5 || undefined,
                '--site-cor6': site?.cor6 || undefined,
                '--site-cor7': site?.cor7 || undefined,
                '--site-cor8': site?.cor8 || undefined,
                '--site-cor9': site?.cor9 || undefined,
                '--site-cor10': site?.cor10 || undefined,
                '--site-cor11': site?.cor11 || undefined,
                '--site-cor12': site?.cor12 || undefined,
                '--site-cor13': site?.cor13 || undefined,
                '--site-cor14': site?.cor14 || undefined,
                '--site-cor15': site?.cor15 || undefined,
                '--theme-bg': background,
                '--theme-paper': paper,
                '--theme-surface-soft': surfaceSoft,
                '--theme-text': text,
                '--theme-muted': muted,
                '--theme-line': line,
                '--theme-accent': accent,
                '--theme-accent-soft': accentSoft,
                '--theme-accent-ink': accentInk,
                '--theme-accent-soft-ink': accentSoftInk,
                '--theme-secondary-ink': secondaryInk,
                '--theme-whatsapp-ink': whatsappInk,
                '--theme-map-band-ink': mapBandInk,
                '--theme-map-button-bg': mapButtonBg,
                '--theme-map-button-ink': mapButtonInk,
                '--theme-map-button-border': mapButtonBorder
            } as CSSProperties;
        },
        [
            site?.cor1,
            site?.cor2,
            site?.cor3,
            site?.cor4,
            site?.cor5,
            site?.cor6,
            site?.cor7,
            site?.cor8,
            site?.cor9,
            site?.cor10,
            site?.cor11,
            site?.cor12,
            site?.cor13,
            site?.cor14,
            site?.cor15
        ]
    );

    const empreendimentoInicial = empreendimento && isEmpreendimentoVisibleOnSite(empreendimento?.mostrar_site)
        ? empreendimento
        : null;
    const [empreendimentoAtual, setEmpreendimentoAtual] = useState<EmpreendimentoData | null>(empreendimentoInicial);
    const [carregando, setCarregando] = useState<boolean>(!empreendimentoInicial);
    const [propertiesRelatedActual, setPropertiesRelatedActual] = useState<any[]>([]);
    const [plantaAtivaIndex, setPlantaAtivaIndex] = useState<number>(0);
    const [modalAberto, setModalAberto] = useState<boolean>(false);
    const [modalInitialIndex, setModalInitialIndex] = useState<number>(0);
    const [openMapa, setOpenMapa] = useState<number>(0);
    const [leadModalOpen, setLeadModalOpen] = useState<boolean>(false);

    const favoritoList = Array.isArray(favorito) ? favorito : [];
    const allCities = allDataHome?.cidades ? formatSelects(allDataHome?.cidades) : [];
    useEffect(() => {
        const empreendimentoValido = empreendimento && isEmpreendimentoVisibleOnSite(empreendimento?.mostrar_site)
            ? empreendimento
            : null;
        setEmpreendimentoAtual(empreendimentoValido);
        setCarregando(!empreendimentoValido);
        setPlantaAtivaIndex(0);
    }, [empreendimento]);

    useEffect(() => {
        if (!router.isReady) return;

        const id = parseIdParam(router.query.id);
        if (!id) {
            setCarregando(false);
            return;
        }

        if (empreendimentoAtual?.id === id) {
            setCarregando(false);
            return;
        }

        let isActive = true;
        setCarregando(true);

        getEmpreendimentoById(id, prodOrigin, codsite)
            .then((response) => {
                if (!isActive) return;
                const empreendimentoResponse = response?.empreendimento || null;
                setEmpreendimentoAtual(
                    empreendimentoResponse && isEmpreendimentoVisibleOnSite(empreendimentoResponse?.mostrar_site)
                        ? empreendimentoResponse
                        : null
                );
                setPlantaAtivaIndex(0);
            })
            .catch(() => {
                if (!isActive) return;
                setEmpreendimentoAtual(null);
            })
            .finally(() => {
                if (!isActive) return;
                setCarregando(false);
            });

        return () => {
            isActive = false;
        };
    }, [codsite, empreendimentoAtual?.id, prodOrigin, router.isReady, router.query.id]);

    useEffect(() => {
        if (!empreendimentoAtual?.id) {
            setPropertiesRelatedActual([]);
            return;
        }

        let isActive = true;

        const getRelatedProperties = async () => {
            try {
                const response = await imoveis_empreendimento(empreendimentoAtual.id, prodOrigin, codsite);
                if (!isActive) return;
                setPropertiesRelatedActual(response?.imoveis?.imoveis || []);
            } catch (_error) {
                if (!isActive) return;
                setPropertiesRelatedActual([]);
            }
        };

        getRelatedProperties();

        return () => {
            isActive = false;
        };
    }, [codsite, empreendimentoAtual?.id, prodOrigin]);

    useEffect(() => {
        if (!modalAberto || typeof document === 'undefined') return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [modalAberto]);

    const fotosOrdenadas = useMemo(() => {
        const photos = [...(empreendimentoAtual?.fotos || [])].filter((foto) => Boolean(foto?.url_foto));
        return photos.sort((a, b) => Number(a.posicao || 0) - Number(b.posicao || 0));
    }, [empreendimentoAtual?.fotos]);

    const fotosPlantas = useMemo(() => fotosOrdenadas.filter((foto) => isPlantaFoto(foto)), [fotosOrdenadas]);

    const fotosGaleria = useMemo(() => {
        const fotosSemPlanta = fotosOrdenadas.filter((foto) => !isPlantaFoto(foto));
        return fotosSemPlanta.length > 0 ? fotosSemPlanta : fotosOrdenadas;
    }, [fotosOrdenadas]);

    useEffect(() => {
        if (plantaAtivaIndex <= fotosPlantas.length - 1) return;
        setPlantaAtivaIndex(0);
    }, [fotosPlantas.length, plantaAtivaIndex]);

    const fotoDestaque = useMemo(() => {
        const destaque = fotosOrdenadas.find((foto) => Number(foto?.destaque) === 1);
        return destaque || fotosGaleria[0] || fotosOrdenadas[0] || null;
    }, [fotosGaleria, fotosOrdenadas]);

    const faixaPreco = formatPriceRange(empreendimentoAtual?.preco_min, empreendimentoAtual?.preco_max);
    const precoInicial = formatCurrency(empreendimentoAtual?.preco_min) || formatCurrency(empreendimentoAtual?.preco_max);
    const faixaArea = formatAreaRange(
        empreendimentoAtual?.area_privativa_min,
        empreendimentoAtual?.are_privativa_min,
        empreendimentoAtual?.area_privativa_max
    );

    const tiposEmpreendimentoNomes = useMemo(() => {
        if (!Array.isArray(empreendimentoAtual?.tipos_emp_detalhes)) return [];
        const tiposDetalhes = empreendimentoAtual?.tipos_emp_detalhes || [];
        return uniqueOrdered(
            tiposDetalhes
                .map((item) => String(item?.nome || '').trim())
                .filter(Boolean)
        );
    }, [empreendimentoAtual?.tipos_emp_detalhes]);

    const tipoNome = useMemo(() => {
        if (tiposEmpreendimentoNomes.length > 0) {
            return tiposEmpreendimentoNomes[0];
        }

        const idTipo = empreendimentoAtual?.id_tipos;
        if (!idTipo || !allDataHome?.tipos?.length) return '';
        const type = allDataHome.tipos.find((item: any) => Number(item?.id) === Number(idTipo));
        return type?.nome || '';
    }, [allDataHome?.tipos, empreendimentoAtual?.id_tipos, tiposEmpreendimentoNomes]);

    const tiposEmpreendimento = useMemo(() => {
        if (tiposEmpreendimentoNomes.length > 0) {
            return tiposEmpreendimentoNomes;
        }
        return tipoNome ? [tipoNome] : [];
    }, [tipoNome, tiposEmpreendimentoNomes]);

    const categoriaEmpreendimento = useMemo(
        () => String(empreendimentoAtual?.categoria_empreendimento?.nome || '').trim(),
        [empreendimentoAtual?.categoria_empreendimento?.nome]
    );

    const ufEmpreendimento = useMemo(() => {
        const uf = String(empreendimentoAtual?.uf || '').trim();
        if (!uf) return '';

        const idEstado = Number(empreendimentoAtual?.id_estado || 0);
        const nomeEstado = String(empreendimentoAtual?.Estado || empreendimentoAtual?.estado || '').trim();

        if (uf.toUpperCase() === 'RS' && !idEstado && !nomeEstado) {
            return '';
        }

        return uf;
    }, [empreendimentoAtual?.Estado, empreendimentoAtual?.estado, empreendimentoAtual?.id_estado, empreendimentoAtual?.uf]);

    const localizacaoCompleta = useMemo(() => {
        const cidadeUf = [normalizeLocationPart(empreendimentoAtual?.cidade), normalizeLocationPart(ufEmpreendimento)]
            .filter(Boolean)
            .join(' - ');
        return joinLocationParts([empreendimentoAtual?.endereco, empreendimentoAtual?.bairro, cidadeUf]);
    }, [empreendimentoAtual?.bairro, empreendimentoAtual?.cidade, empreendimentoAtual?.endereco, ufEmpreendimento]);

    const resumoTags = useMemo(() => {
        const tags: Array<{ key: string; icon: ResumoIconType; label: string }> = [
            {
                key: 'dormitorios',
                icon: 'dormitorios',
                label: empreendimentoAtual?.dormitorios
                    ? `${empreendimentoAtual?.dormitorios} dorms`
                    : empreendimentoAtual?.suites
                      ? `${empreendimentoAtual?.suites} suíte${empreendimentoAtual?.suites > 1 ? 's' : ''}`
                      : ''
            },
            { key: 'area', icon: 'area', label: (faixaArea || '').replace('à', 'a') },
            { key: 'banheiros', icon: 'banheiros', label: empreendimentoAtual?.banheiros ? `até ${empreendimentoAtual?.banheiros} Banheiros` : '' },
            { key: 'vagas', icon: 'vagas', label: empreendimentoAtual?.vagas ? `${empreendimentoAtual?.vagas} Vagas` : '' },
            { key: 'bairro', icon: 'bairro', label: empreendimentoAtual?.bairro ? `Bairro ${empreendimentoAtual?.bairro}` : '' }
        ];

        return tags.filter((tag) => Boolean(tag.label));
    }, [
        empreendimentoAtual?.bairro,
        empreendimentoAtual?.banheiros,
        empreendimentoAtual?.dormitorios,
        empreendimentoAtual?.suites,
        empreendimentoAtual?.vagas,
        faixaArea
    ]);

    const descricaoHtml = empreendimentoAtual?.descricao || empreendimentoAtual?.Descricao_interna || '<p>Descrição indisponível.</p>';
    const caracteristicas = useMemo(() => {
        const caracteristicasApi = Array.isArray(empreendimentoAtual?.caracteristicas_detalhes)
            ? (empreendimentoAtual?.caracteristicas_detalhes || [])
                .map((item) => normalizeFeatureText(String(item?.nome || '')))
                .filter(Boolean)
            : [];

        if (caracteristicasApi.length > 0) {
            return uniqueOrdered(caracteristicasApi).slice(0, 24);
        }

        return extractCaracteristicas(descricaoHtml);
    }, [descricaoHtml, empreendimentoAtual?.caracteristicas_detalhes]);

    const allDepoimentos = useMemo(
        () => (allDataHome?.depoimentos || []).filter((item: any) => item?.depoimento?.length),
        [allDataHome?.depoimentos]
    );
    const telefoneRaw = site?.Dados?.tel1_whats || site?.Dados?.cel1 || site?.Dados?.tel1 || site?.Dados?.tel2 || '';
    const telefone = normalizePhone(telefoneRaw);

    const whatsappContatoLink = telefone
        ? `https://api.whatsapp.com/send?phone=${telefone}&text=${encodeURIComponent(
              `Ola, tenho interesse no empreendimento ${empreendimentoAtual?.nome || ''}.`
          )}`
        : '';

    const whatsappVisitaLink = telefone
        ? `https://api.whatsapp.com/send?phone=${telefone}&text=${encodeURIComponent(
              `Ola, quero agendar uma visita no empreendimento ${empreendimentoAtual?.nome || ''}.`
          )}`
        : '';

    const telefoneLink = telefone ? `tel:+${telefone}` : '';
    const linkAgendamento = whatsappVisitaLink || telefoneLink;
    const agendamentoEmNovaAba = Boolean(whatsappVisitaLink);
    const leadModalMessage = empreendimentoAtual?.nome
        ? `Tenho interesse no empreendimento ${empreendimentoAtual.nome}.`
        : 'Tenho interesse neste empreendimento.';

    const mapProperties = useMemo(() => {
        if (!Array.isArray(propertiesRelatedActual)) return [];

        return propertiesRelatedActual
            .map((item) => {
                const latitude_longitude = toMapaLatLng(item?.latitude_longitude);
                if (!latitude_longitude) return null;

                return {
                    ...item,
                    id: item?.id || 0,
                    referencia: item?.referencia || item?.id || empreendimentoAtual?.id || 0,
                    latitude_longitude,
                    cidade: item?.cidade || { nome: empreendimentoAtual?.cidade || '' },
                    bairro: item?.bairro || { nome: empreendimentoAtual?.bairro || '' },
                    tipo: item?.tipo || { nome: tipoNome || 'Imovel' },
                    fotos: Array.isArray(item?.fotos) && item.fotos.length ? item.fotos : [{ url_foto: fotoDestaque?.url_foto || fallbackImage }]
                };
            })
            .filter(Boolean);
    }, [
        empreendimentoAtual?.bairro,
        empreendimentoAtual?.cidade,
        empreendimentoAtual?.id,
        fotoDestaque?.url_foto,
        propertiesRelatedActual,
        tipoNome
    ]);

    const mapPrimaryProperty = useMemo(() => {
        if (mapProperties.length > 0) return mapProperties[0];

        const latitude_longitude = toMapaLatLng(empreendimentoAtual?.latitude_longitude);
        if (!latitude_longitude) return null;

        return {
            id: empreendimentoAtual?.id || 0,
            referencia: empreendimentoAtual?.id || 0,
            latitude_longitude,
            cidade: { nome: empreendimentoAtual?.cidade || '' },
            bairro: { nome: empreendimentoAtual?.bairro || '' },
            tipo: { nome: tipoNome || 'Empreendimento' },
            fotos: [{ url_foto: fotoDestaque?.url_foto || fallbackImage }]
        };
    }, [
        empreendimentoAtual?.bairro,
        empreendimentoAtual?.cidade,
        empreendimentoAtual?.id,
        empreendimentoAtual?.latitude_longitude,
        fotoDestaque?.url_foto,
        mapProperties,
        tipoNome
    ]);

    const temMapa = Boolean(mapPrimaryProperty?.latitude_longitude);

    const pagePath = (router.asPath || asPath || `/empreendimento/${empreendimentoAtual?.id || ''}`).split('?')[0];
    const canonicalHost = String(prodOrigin || site?.site || (typeof window !== 'undefined' ? window.location.host : '')).trim();
    const baseUrl = buildCanonical(canonicalHost, '/').replace(/\/$/, '');
    const canonicalUrl = buildCanonical(canonicalHost, pagePath);
    const withCodsite = (path: string) => appendCodsiteQuery(path, codsite, canonicalHost);

    const descricaoSeoBase =
        empreendimentoAtual?.meta_description || stripHtml(empreendimentoAtual?.descricao || empreendimentoAtual?.Descricao_interna);
    const descricaoSeo = limitText(
        descricaoSeoBase || `Conheca o empreendimento ${empreendimentoAtual?.nome || ''} em ${empreendimentoAtual?.cidade || ''}.`,
        160
    );
    const tituloSeo =
        empreendimentoAtual?.meta_title ||
        `${empreendimentoAtual?.nome || 'Empreendimento'} | ${site?.titulo || site?.Dados?.empresa || 'Imobiliaria'}`;
    const palavrasChaveSeo =
        empreendimentoAtual?.meta_keywords ||
        [empreendimentoAtual?.nome, empreendimentoAtual?.cidade, empreendimentoAtual?.bairro, 'empreendimento', 'lancamento imobiliario']
            .filter(Boolean)
            .join(', ');

    const empreendimentoSchema: Record<string, any> = {
        '@context': 'https://schema.org',
        '@type': 'ApartmentComplex',
        name: empreendimentoAtual?.nome || '',
        description: descricaoSeo,
        url: canonicalUrl,
        image: fotosOrdenadas.slice(0, 8).map((item) => item.url_foto),
        address: {
            '@type': 'PostalAddress',
            streetAddress: empreendimentoAtual?.endereco || '',
            addressLocality: empreendimentoAtual?.cidade || '',
            addressRegion: ufEmpreendimento,
            postalCode: empreendimentoAtual?.cep || '',
            addressCountry: 'BR'
        }
    };

    if (empreendimentoAtual?.preco_min || empreendimentoAtual?.preco_max) {
        empreendimentoSchema.offers = {
            '@type': 'AggregateOffer',
            priceCurrency: 'BRL',
            lowPrice: Number(empreendimentoAtual?.preco_min || 0),
            highPrice: Number(empreendimentoAtual?.preco_max || empreendimentoAtual?.preco_min || 0)
        };
    }

    const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Inicio', item: baseUrl ? `${baseUrl}/` : '/' },
            { '@type': 'ListItem', position: 2, name: 'Empreendimentos', item: baseUrl ? `${baseUrl}/condominios` : '/condominios' },
            { '@type': 'ListItem', position: 3, name: empreendimentoAtual?.nome || 'Empreendimento', item: canonicalUrl }
        ]
    };

    const renderRodape = () => {
        switch (site?.rodape) {
            case 1:
                return (
                    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true, amount: 0.22 }}>
                        <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite} origin={prodOrigin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favoritoList} />
                    </motion.div>
                );
            case 2:
                return <AboutPieces allRodape={allDataHome?.rodape} site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite} origin={prodOrigin} isMobile={isMobile1} />;
            case 3:
                return <AboutPieces2 site={site} allRodape={allDataHome?.rodape} />;
            default:
                return (
                    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true, amount: 0.22 }}>
                        <AboutPieces1 site={site} allCities={allCities} allCitiesTipes={allDataHome?.tipos} codsite={codsite} origin={prodOrigin} isMobile={isMobile1} allRodape={allDataHome?.rodape} favorito={favoritoList} />
                    </motion.div>
                );
        }
    };

    if (carregando) {
        return (
            <>
                {site ? <WelcomePiecesProperties2 site={site} favorito={favoritoList} props={allDataHome} /> : null}
                <div className={styles.loadingWrap}><div className={styles.loadingBox}><div className={styles.loadingPulse} /><p>Carregando empreendimento...</p></div></div>
                {site ? renderRodape() : null}
            </>
        );
    }

    if (!empreendimentoAtual?.id) {
        return (
            <>
                <Head><title>Empreendimento nao encontrado</title><meta name="robots" content="noindex, nofollow" /></Head>
                {site ? <WelcomePiecesProperties2 site={site} favorito={favoritoList} props={allDataHome} /> : null}
                <section className={styles.emptyWrap}><h1>Empreendimento nao encontrado</h1><p>Nao foi possivel carregar os dados deste empreendimento.</p><Link href={withCodsite('/condominios')} className={styles.emptyAction}>Voltar para empreendimentos</Link></section>
                {site ? renderRodape() : null}
            </>
        );
    }
    return (
        <>
            <Head>
                <title>{tituloSeo}</title>
                <meta name="description" content={descricaoSeo} />
                <meta name="keywords" content={palavrasChaveSeo} />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content={tituloSeo} />
                <meta property="og:description" content={descricaoSeo} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={fotoDestaque?.url_foto || fallbackImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={tituloSeo} />
                <meta name="twitter:description" content={descricaoSeo} />
                <meta name="twitter:image" content={fotoDestaque?.url_foto || fallbackImage} />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(empreendimentoSchema) }} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
            </Head>

            {site ? <WelcomePiecesProperties2 site={site} favorito={favoritoList} props={allDataHome} /> : null}

            <div className={styles.page} style={pageThemeVars}>
                <section id="empreendimento" className={styles.heroSection}>
                    <div className={styles.heroMedia}>
                        <Image src={fotoDestaque?.url_foto || fallbackImage} alt={empreendimentoAtual.nome} fill priority sizes="100vw" className={styles.heroImage} />
                        <div className={styles.heroShade} />

                        <div className={styles.heroContent}>
                            <div className={styles.heroBreadcrumbs}>
                                <Link href={withCodsite('/')}>Início</Link><span>/</span><Link href={withCodsite('/condominios')}>Empreendimentos</Link><span>/</span><strong>{empreendimentoAtual.nome}</strong>
                            </div>

                            <p className={styles.heroStatus}>{formatSituacao(empreendimentoAtual?.situacao)}</p>
                            <h1 className={styles.heroTitle}>{empreendimentoAtual.nome}</h1>

                            {localizacaoCompleta ? (
                                <p className={styles.heroLocation}><HiMapPin size={18} /><span>{localizacaoCompleta}</span></p>
                            ) : null}

                            <div className={styles.heroFacts}>
                                {faixaArea ? <div className={styles.heroFact}><span>Área privativa</span><strong>{faixaArea}</strong></div> : null}
                                {faixaPreco ? <div className={styles.heroFact}><span>Faixa de preço</span><strong>{faixaPreco}</strong></div> : null}
                            </div>
                        </div>

                        {empreendimentoAtual?.logo ? (
                            <div className={styles.heroLogoBox}>
                                <Image src={empreendimentoAtual.logo} alt={`Logo ${empreendimentoAtual.nome}`} fill sizes="240px" className={styles.heroLogoImage} />
                            </div>
                        ) : null}
                    </div>

                    <aside id="contato" className={styles.contactCard}>
                        <p className={styles.contactFlag}>{formatSituacao(empreendimentoAtual?.situacao)}</p>
                        <p className={styles.contactLabel}>A partir de</p>
                        <h2 className={styles.contactPrice}>{precoInicial || 'Consulte valores'}</h2>

                        <div className={styles.contactActions}>
                            {whatsappContatoLink ? <a href={whatsappContatoLink} target="_blank" rel="noreferrer" className={styles.primaryAction}>Fale Conosco</a> : null}

                            {linkAgendamento ? (
                                <a href={linkAgendamento} target={agendamentoEmNovaAba ? '_blank' : undefined} rel={agendamentoEmNovaAba ? 'noreferrer' : undefined} className={styles.secondaryAction}>
                                    Agende Uma Visita
                                </a>
                            ) : null}

                            <button
                                type="button"
                                className={styles.whatsappAction}
                                onClick={() => setLeadModalOpen(true)}
                            >
                                Receba Contato Em 3 Minutos
                            </button>
                        </div>
                    </aside>
                </section>

                <div className={styles.contentShell}>
                    <nav className={styles.sectionNav}>
                        <a href="#empreendimento" className={styles.sectionLink}>O Empreendimento</a>
                        {fotosPlantas.length > 0 ? <a href="#plantas" className={styles.sectionLink}>Plantas</a> : null}
                        <a href="#galeria" className={styles.sectionLink}>Galeria</a>
                        <a href="#unidades" className={styles.sectionLink}>Unidades</a>
                        <a href="#contato" className={styles.sectionLink}>Contato</a>
                        {temMapa ? <a href="#mapa" className={styles.sectionLink}>Mapa</a> : null}
                    </nav>

                    {resumoTags.length > 0 ? (
                        <div className={styles.tagsRow}>
                            {resumoTags.map((tag) => (
                                <span key={tag.key} className={styles.infoTag}>
                                    <ResumoTagIcon type={tag.icon} />
                                    <span>{tag.label}</span>
                                </span>
                            ))}
                        </div>
                    ) : null}

                    {categoriaEmpreendimento || tiposEmpreendimento.length > 0 ? (
                        <div className={styles.tagsRow}>
                            {categoriaEmpreendimento ? (
                                <span className={styles.infoTag}>
                                    <span><strong>Categoria:</strong> {categoriaEmpreendimento}</span>
                                </span>
                            ) : null}
                            {tiposEmpreendimento.length > 0 ? (
                                <span className={styles.infoTag}>
                                    <span><strong>Tipos:</strong> {tiposEmpreendimento.join(', ')}</span>
                                </span>
                            ) : null}
                        </div>
                    ) : null}

                    {fotosPlantas.length > 0 ? (
                        <section id="plantas" className={styles.sectionBlock}>
                            <header className={styles.sectionHeader}>
                                <h2>Plantas do empreendimento</h2>
                                <p>Selecione uma planta para visualizar.</p>
                            </header>

                            <div className={styles.plantaTabs}>
                                {fotosPlantas.map((foto, index) => (
                                    <button key={`${foto.id}-${index}`} type="button" className={`${styles.plantaTab} ${index === plantaAtivaIndex ? styles.plantaTabActive : ''}`} onClick={() => setPlantaAtivaIndex(index)}>
                                        {foto?.descricao?.trim() || `Planta ${index + 1}`}
                                    </button>
                                ))}
                            </div>

                            <div className={styles.plantaViewer}>
                                <Image src={fotosPlantas[plantaAtivaIndex]?.url_foto || fallbackImage} alt={fotosPlantas[plantaAtivaIndex]?.descricao || `Planta ${plantaAtivaIndex + 1}`} fill sizes="(max-width: 1200px) 100vw, 1080px" className={styles.plantaImage} />
                            </div>
                        </section>
                    ) : null}

                    <section id="galeria" className={styles.sectionBlock}>
                        <header className={styles.sectionHeader}>
                            <h2>Galeria de fotos</h2>
                            <p>Clique em uma foto para abrir o slider em fullscreen.</p>
                        </header>

                        {fotosGaleria.length > 0 ? (
                            <div className={styles.galleryGrid}>
                                {fotosGaleria.slice(0, 5).map((foto, index) => (
                                    <button key={`${foto.id}-${index}`} type="button" className={`${styles.galleryItem} ${index === 0 ? styles.galleryMain : ''}`} onClick={() => { setModalInitialIndex(index); setModalAberto(true); }}>
                                        <Image src={foto.url_foto} alt={foto.descricao || `Foto ${index + 1}`} fill sizes={index === 0 ? '(max-width: 1024px) 100vw, 62vw' : '(max-width: 1024px) 50vw, 26vw'} className={styles.galleryImage} />
                                        {index === 4 && fotosGaleria.length > 5 ? <span className={styles.galleryMore}>+{fotosGaleria.length - 5}</span> : null}
                                    </button>
                                ))}
                            </div>
                        ) : <p className={styles.emptyMessage}>Nenhuma foto encontrada para este empreendimento.</p>}
                    </section>

                    <section className={styles.sectionBlock}>
                        <header className={styles.sectionHeader}><h2>Descrição e características do empreendimento</h2></header>

                        <div className={styles.descriptionLayout}>
                            <div className={styles.descriptionMain}>
                                <div className={styles.descriptionHtml} dangerouslySetInnerHTML={{ __html: descricaoHtml }} />

                                <div className={styles.featuresBlock}>
                                    <h3>Características</h3>
                                    {caracteristicas.length > 0 ? (
                                        <ul className={styles.featuresGrid}>{caracteristicas.map((item) => <li key={item}>{item}</li>)}</ul>
                                    ) : <p className={styles.emptyMessage}>Entre em contato para receber a lista completa de características.</p>}
                                </div>
                            </div>
                        </div>
                    </section>

                    {propertiesRelatedActual?.length > 0 ? (
                        <section id="unidades" className={`${styles.sectionBlock} ${styles.unitsSection}`}>
                            <div className={styles.unitsCarousel}>
                                <RecommendationPiecesProperties propertiesRelatedActual={propertiesRelatedActual} site={site} favorito={favoritoList} setFavorito={setFavorito} imoveisEmpreendimento={true} allViewProperties={null} />
                            </div>
                        </section>
                    ) : null}
                    
                    {temMapa ? (
                        <section id="mapas" className={styles.mapSection}>
                            <div className={styles.mapBand}>
                                <div className={styles.mapAddressBlock}>
                                    <p className={styles.mapLabel}>Localização:</p>
                                    <strong>{localizacaoCompleta || `${empreendimentoAtual?.cidade || ''}`}</strong>
                                </div>

                                <div className={styles.mapActions}><button type="button" className={styles.mapButton} onClick={() => setOpenMapa(1)}>Mapa</button></div>
                            </div>
                        </section>
                    ) : null}

                    {allDepoimentos.length > 0 && site?.modelo_depoimento > 0 ? (
                        <section className={styles.depoimentosWrap}>
                            {site?.modelo_depoimento === 2 ? (
                                <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true, amount: 0.22 }}>
                                    <DepoimentoCard2 slides={allDepoimentos} title="DEPOIMENTOS" site={site} />
                                </motion.div>
                            ) : (
                                <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} viewport={{ once: true, amount: 0.22 }}>
                                    <DepoimentoCard slides={allDepoimentos} title="DEPOIMENTOS" site={site} />
                                </motion.div>
                            )}
                        </section>
                    ) : null}
                </div>
                {modalAberto && fotosGaleria.length > 0 ? (
                    <div className={styles.modalBackdrop} onClick={() => setModalAberto(false)}>
                        <button type="button" className={styles.modalClose} onClick={() => setModalAberto(false)} aria-label="Fechar galeria">
                            <IoCloseSharp size={28} />
                        </button>

                        <div className={styles.modalFrame} onClick={(event) => event.stopPropagation()}>
                            <Swiper key={`${modalInitialIndex}-${fotosGaleria.length}`} modules={[Navigation, Keyboard]} navigation keyboard={{ enabled: true }} initialSlide={modalInitialIndex} className={styles.modalSwiper}>
                                {fotosGaleria.map((foto, index) => (
                                    <SwiperSlide key={`${foto.id}-${index}`} className={styles.modalSlide}>
                                        <div className={styles.modalImageWrap}>
                                            <Image src={foto.url_foto} alt={foto?.descricao || `Foto ${index + 1}`} fill sizes="100vw" className={styles.modalImage} />
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                    </div>
                ) : null} 

                {temMapa && openMapa && mapPrimaryProperty && openMapa ? (
                    <Mapa
                        prop={mapProperties[0] || mapPrimaryProperty}
                        properties={mapProperties.length > 0 ? mapProperties : [mapPrimaryProperty]}
                        tipo={0}
                        popup={0}
                        openMapa={1}
                        setOpenMapa={setOpenMapa}
                    />
                ) : null}

                {site ? (
                    <LeadContactModal
                        open={leadModalOpen}
                        onClose={() => setLeadModalOpen(false)}
                        site={site}
                        origin={prodOrigin}
                        codsite={codsite}
                        title="Receba contato em 3 minutos"
                        message={leadModalMessage}
                        source="empreendimento-receba-3min"
                        entityId={0}
                    />
                ) : null}
            </div>

            {site ? renderRodape() : null}
        </>
    );
};

export default EmpreendimentoPage;
